/**
 * ResizeController for O2D Grid Layout
 * Monitors container size changes and calculates visible grid dimensions
 */

import type {ReactiveController} from 'lit';
import {O2dLayoutElement} from "../elements/o2d-layout.element";

/**
 * Event detail for layout-size-changed event
 */
export interface LayoutChangedDetail {
    containerWidth: number;
    containerHeight: number;
    rowsCount: number,
    columnsCount: number
}

/**
 * ResizeController class
 * Monitors container resizing and dispatches layout change events
 */
export class ResizeController implements ReactiveController {
    private resizeObserver: ResizeObserver | null = null;
    private throttleTimeout: number | null = null;
    private readonly throttleDelay = 12.5; // ms

    constructor(private host: O2dLayoutElement) {
        this.host.addController(this);
    }

    /**
     * Called when the host is connected to the DOM
     */
    hostConnected(): void {
        this.initializeResizeObserver();
    }

    /**
     * Called when the host is disconnected from the DOM
     */
    hostDisconnected(): void {
        this.cleanup();
    }

    /**
     * Initialize ResizeObserver
     */
    private initializeResizeObserver(): void {
        if (typeof ResizeObserver === 'undefined') {
            console.warn('ResizeObserver is not available in this browser');
            return;
        }

        this.resizeObserver = new ResizeObserver((entries) => {
            this.handleResize(entries);
        });

        this.resizeObserver.observe(this.host);

    }

    /**
     * Handle resize observations
     */
    private handleResize(entries: ResizeObserverEntry[]): void {
        if (entries.length === 0) return;

        const entry = entries[0];
        if (!entry) return;

        this.throttledDispatchLayoutChange();
    }

    /**
     * Throttled dispatch of layout change event
     */
    private throttledDispatchLayoutChange(): void {
        // Clear existing timeout
        if (this.throttleTimeout !== null) {
            clearTimeout(this.throttleTimeout);
        }

        // If this is the first call or enough time has passed, dispatch immediately
        const shouldDispatchImmediately =
            !this.host.dimensions ||
            !this.throttleTimeout;

        if (shouldDispatchImmediately) {
            this.dispatchLayoutChange();

            // Set timeout to prevent immediate subsequent calls
            this.throttleTimeout = window.setTimeout(() => {
                this.throttleTimeout = null;
            }, this.throttleDelay);
        } else {
            // Schedule dispatch after throttle delay
            this.throttleTimeout = window.setTimeout(() => {
                this.dispatchLayoutChange();
                this.throttleTimeout = null;
            }, this.throttleDelay);
        }
    }

    /**
     * Calculate dimensions and dispatch layout change event
     */
    private dispatchLayoutChange(): void {
        this.host.dispatchGridChangedEvent();
    }

    /**
     * Clean up resources
     */
    private cleanup(): void {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }

        if (this.throttleTimeout !== null) {
            clearTimeout(this.throttleTimeout);
            this.throttleTimeout = null;
        }
    }
}